import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@saas-kit/lib';
import { Card, CardContent, CardHeader, CardTitle } from '@saas-kit/ui';

import { fetchBranding } from './apiBranding';
import { QmsPrintPortal } from './QmsPrintPortal';
import { QmsTokenReceipt } from './QmsTokenReceipt';
import type { WorkstationState } from './useWorkstation';
import { WorkstationActions } from './WorkstationActions';

/** Average `total / count` rendered as `m:ss`. */
function avg(total: number, count: number): string {
  if (count <= 0) return '0:00';
  const secs = Math.round(total / count);
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: string | number;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-semibold text-foreground">{value}</span>
    </div>
  );
}

/** The claimed-counter layout: waiting queue, current token + actions,
 *  and today's session stats — plus the hidden print receipt. */
export function WorkstationView({ ws }: { ws: WorkstationState }): JSX.Element {
  const { t, i18n } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const branding = useQuery({
    queryKey: ['qms-branding', tenantId],
    queryFn: fetchBranding,
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const snapshot = ws.workstation;
  const current = ws.current;
  const session = snapshot?.session ?? ws.session;
  const waiting = snapshot?.waiting ?? [];
  const serviceName =
    ws.services.find((s) => s.id === current?.service_id)?.name ?? '';

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-[18rem_1fr_18rem]">
        <Card>
          <CardHeader>
            <CardTitle>
              {t('workstation.queue.title')} ·{' '}
              {t('workstation.queue.count', {
                count: snapshot?.waiting_count ?? 0,
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {waiting.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('workstation.queue.empty')}
              </p>
            ) : (
              <ul className="space-y-2">
                {waiting.map((token) => (
                  <li
                    key={token.id}
                    className="flex items-baseline justify-between gap-2 text-sm"
                  >
                    <span className="font-mono font-semibold text-foreground">
                      {token.display_code}
                    </span>
                    <span className="truncate text-muted-foreground">
                      {token.customer_name ?? ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
              {current ? (
                <>
                  <p className="text-sm uppercase tracking-wide text-muted-foreground">
                    {t('workstation.current.label')}
                  </p>
                  <p className="font-mono text-6xl font-bold text-foreground">
                    {current.display_code}
                  </p>
                  <p className="text-foreground">{serviceName}</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('workstation.current.idle')}
                </p>
              )}
            </CardContent>
          </Card>
          <WorkstationActions ws={ws} current={current} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('workstation.stats.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Stat
              label={t('workstation.stats.served')}
              value={session?.served_count ?? 0}
            />
            <Stat
              label={t('workstation.stats.no_show')}
              value={session?.no_show_count ?? 0}
            />
            <Stat
              label={t('workstation.stats.avg_wait')}
              value={avg(
                session?.total_wait_seconds ?? 0,
                session?.served_count ?? 0,
              )}
            />
            <Stat
              label={t('workstation.stats.avg_serve')}
              value={avg(
                session?.total_serve_seconds ?? 0,
                session?.served_count ?? 0,
              )}
            />
          </CardContent>
        </Card>
      </div>

      {current ? (
        <QmsPrintPortal>
          <QmsTokenReceipt
            token={current}
            serviceName={serviceName}
            branding={branding.data ?? null}
            locale={i18n.language}
          />
        </QmsPrintPortal>
      ) : null}
    </>
  );
}
